import { Button, Input, Modal, Select, NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BarPanel } from '../components/barPanel/BarPanel';
import { DenseTable } from '../components/denseTable/DenseTable';
import { DenseTableColumn } from '../components/denseTable/DenseTable.types';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { StateChip } from '../components/stateChip/StateChip';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';
import { dodCommandLabel } from '../components/dodCommands';
import { formatTimestamp } from '../overviewTab/dodFormat';

import * as Styled from './DlqTab.styles';
import { DlqTabProps } from './DlqTab.types';
import {
  DLQ_ERROR_CLASSES,
  DLQ_PAGE_SIZES,
  DLQ_REASONS,
  DlqErrorClass,
  DlqReason,
  DlqRow,
  dlqErrorClassState,
  dlqErrorLabel,
} from './dlq.helpers';

const CONFIRM_BODY = {
  retrySelected: 'dod.dlq.confirm.retrySelectedBody',
  retryByFilter: 'dod.dlq.confirm.retryByFilterBody',
  purgeSelected: 'dod.dlq.confirm.purgeSelectedBody',
  purgeRow: 'dod.dlq.confirm.purgeRowBody',
} as const;

export const DlqTab = ({
  rows,
  isLoading,
  isError,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  byReason,
  topErrors,
  isSummaryLoading,
  movedSeries,
  movedSeriesLabels,
  movedTotal,
  isMovedLoading,
  hasTimeseries,
  hasReasonLabels,
  reason,
  errorClass,
  search,
  selectedErrorMessage,
  onReasonChange,
  onErrorClassChange,
  onSearchChange,
  onSelectTopError,
  checkedRows,
  onCheckedChange,
  selectedId,
  onSelectRow,
  onRetrySelected,
  onRetryByFilter,
  onPurgeSelected,
  onExportCsv,
  onExportByFilter,
  isExportingFilter,
  isMutating,
  canModify,
  confirm,
  onConfirm,
  onCancelConfirm,
}: DlqTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  /**
   * Every text column returns a plain string so the shared `Table.RowItem` renders it: that is what
   * gives the ellipsis and the hover tooltip the rest of the application has. Only the chips and
   * the action button are nodes.
   */
  const columns: Array<DenseTableColumn<DlqRow>> = [
    {
      key: 'time',
      width: '150px',
      header: formatMessage({ id: AppMessages['dod.dlq.column.time'] }),
      render: (row) => formatTimestamp(row.timestamp),
    },
    {
      key: 'rcpTkd',
      width: 'minmax(110px, 1.2fr)',
      header: formatMessage({ id: AppMessages['dod.dlq.column.rcpTkd'] }),
      render: (row) => `${row.rcpLabel} · ${row.tkdLabel}`,
    },
    {
      key: 'command',
      width: 'minmax(100px, 1.2fr)',
      header: formatMessage({ id: AppMessages['dod.dlq.column.command'] }),
      render: (row) => dodCommandLabel(String(row.numerRozkazu).padStart(2, '0')),
    },
    {
      key: 'kad',
      width: 'minmax(140px, 1fr)',
      header: formatMessage({ id: AppMessages['dod.dlq.column.kad'] }),
      render: (row) => row.kad,
    },
    {
      key: 'attempts',
      width: 'minmax(55px, 0.4fr)',
      header: formatMessage({ id: AppMessages['dod.dlq.column.attempts'] }),
      render: (row) => `${row.retryCount}/${row.maxRetries}`,
    },
    {
      key: 'reason',
      width: '120px',
      header: formatMessage({ id: AppMessages['dod.dlq.column.reason'] }),
      render: (row) => <StateChip state={row.reason} />,
    },
    {
      key: 'errorClass',
      width: '140px',
      header: formatMessage({ id: AppMessages['dod.dlq.column.errorClass'] }),
      render: (row) => <StateChip state={dlqErrorClassState(row.errorClass)} />,
    },
    {
      key: 'lastError',
      width: 'minmax(90px, 2fr)',
      header: formatMessage({ id: AppMessages['dod.dlq.column.lastError'] }),
      render: (row) => dlqErrorLabel(row),
    },
  ];

  return (
    <>
      <Styled.PanelRow>
        <BarPanel
          title={formatMessage({ id: AppMessages['dod.dlq.panel.byReason'] })}
          data={byReason}
          layout="horizontal"
          height={160}
          isLoading={isSummaryLoading}
        />
        <BarPanel
          title={formatMessage({ id: AppMessages['dod.dlq.panel.topErrors'] })}
          data={topErrors}
          layout="horizontal"
          height={160}
          isLoading={isSummaryLoading}
          selectedId={selectedErrorMessage || undefined}
          onSelect={onSelectTopError}
        />
        {hasTimeseries ? (
          <TimeSeriesPanel
            title={formatMessage({ id: AppMessages['dod.dlq.panel.movedPerHour'] })}
            series={movedSeries}
            seriesLabels={movedSeriesLabels}
            area
            stacked
            height={160}
            isLoading={isMovedLoading}
            actions={
              <span>
                {formatMessage({ id: AppMessages['dod.dlq.panel.movedTotal'] }, { count: movedTotal })} ·{' '}
                {formatMessage({
                  id: AppMessages[hasReasonLabels ? 'dod.dlq.panel.byReason' : 'dod.dlq.panel.byCommandNote'],
                })}
              </span>
            }
          />
        ) : (
          <SectionCard title={formatMessage({ id: AppMessages['dod.dlq.panel.movedPerHour'] })} bodyHeight={160}>
            <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.unavailable'] })} />
          </SectionCard>
        )}
      </Styled.PanelRow>

      <Styled.FilterRow>
        <Styled.FilterField>
          <Select
            label={formatMessage({ id: AppMessages['dod.dlq.filter.reason'] })}
            variant="outlined"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value as DlqReason | '')}
            options={[
              { label: formatMessage({ id: AppMessages['dod.filter.all'] }), value: '' },
              ...DLQ_REASONS.map((value) => ({
                label: formatMessage({ id: AppMessages[`dod.common.state.${value}`] }),
                value,
              })),
            ]}
          />
        </Styled.FilterField>
        <Styled.FilterField>
          <Select
            label={formatMessage({ id: AppMessages['dod.dlq.filter.errorClass'] })}
            variant="outlined"
            value={errorClass}
            onChange={(event) => onErrorClassChange(event.target.value as DlqErrorClass | '')}
            options={[
              { label: formatMessage({ id: AppMessages['dod.filter.all'] }), value: '' },
              ...DLQ_ERROR_CLASSES.map((value) => ({
                label: formatMessage({ id: AppMessages[`dod.common.state.${dlqErrorClassState(value)}`] }),
                value,
              })),
            ]}
          />
        </Styled.FilterField>
        <Styled.SearchField>
          <Input
            variant="outlined"
            placeholder={formatMessage({ id: AppMessages['dod.dlq.filter.search'] })}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            withoutHelperText
            testId="dod-dlq-search"
          />
        </Styled.SearchField>
      </Styled.FilterRow>

      <Styled.Toolbar>
        <Styled.SelectionCount>
          {formatMessage({ id: AppMessages['dod.dlq.toolbar.selectedOf'] }, { selected: checkedRows.length, total })}
        </Styled.SelectionCount>

        {canModify ? (
          <>
            <Button variant="contained" disabled={checkedRows.length === 0 || isMutating} onClick={onRetrySelected}>
              {formatMessage({ id: AppMessages['dod.dlq.toolbar.retrySelected'] })}
            </Button>
            <Button variant="outlined" disabled={total === 0 || isMutating} onClick={onRetryByFilter}>
              {formatMessage({ id: AppMessages['dod.dlq.toolbar.retryByFilter'] }, { count: total })}
            </Button>
            <Button variant="outlined" disabled={checkedRows.length === 0 || isMutating} onClick={onPurgeSelected}>
              {formatMessage({ id: AppMessages['dod.dlq.toolbar.deleteSelected'] })}
            </Button>
          </>
        ) : (
          <Styled.ReadOnlyNote>{formatMessage({ id: AppMessages['dod.dlq.readOnly'] })}</Styled.ReadOnlyNote>
        )}

        <Styled.ToolbarSpacer />

        <Button variant="outlined" onClick={onExportCsv} disabled={rows.length === 0}>
          {formatMessage({ id: AppMessages['dod.dlq.toolbar.exportCsv'] })}
        </Button>

        <Button variant="outlined" onClick={onExportByFilter} disabled={total === 0 || isExportingFilter}>
          {formatMessage({ id: AppMessages['dod.dlq.toolbar.exportByFilter'] }, { count: total })}
        </Button>
      </Styled.Toolbar>

      {isError ? (
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.common.error'] })} />
        </div>
      ) : (
        <DenseTable<DlqRow>
          columns={columns}
          data={rows}
          isLoading={isLoading}
          nothingToShowText={formatMessage({ id: AppMessages['dod.dlq.empty'] })}
          activeRowId={selectedId}
          onClickRow={onSelectRow}
          checkboxSelection={{
            checked: checkedRows,
            toggleChecked: onCheckedChange,
            // The toolbar above already counts the selection and holds its actions.
            withoutCheckedElementsBar: true,
          }}
          pagination={{
            page,
            count: total,
            rowsPerPage: pageSize,
            setPage: onPageChange,
            setRowsPerPage: onPageSizeChange,
            rowsPerPageOptions: DLQ_PAGE_SIZES,
          }}
        />
      )}

      <Modal
        header={formatMessage({ id: AppMessages['dod.dlq.confirm.title'] })}
        open={confirm !== null}
        onClose={onCancelConfirm}
        type="form"
        size="small"
        onSubmit={onConfirm}
        // The button names the action the body asks about, so 'Usuń' confirms a delete and never 'OK'.
        submitButtonText={formatMessage({
          id: AppMessages[confirm?.action.startsWith('purge') ? 'dod.dlq.purge' : 'dod.dlq.retry'],
        })}
      >
        <span>
          {confirm && formatMessage({ id: AppMessages[CONFIRM_BODY[confirm.action]] }, { count: confirm.count })}
        </span>
      </Modal>
    </>
  );
};
